// src/pages/finance-portal/float-management/FloatManagement.js
//
// Float Management
//
// Tracks collection float (deposit-to-availability lag) and disbursement
// float (check / ACH / wire clearing time). Surfaces aggregates and an
// outstanding-items log.

import React, { useCallback, useEffect, useState } from 'react';
import {
    Alert, Box, Button, Card, CardContent, Chip, Dialog, DialogActions,
    DialogContent, DialogTitle, Grid, IconButton, MenuItem, Skeleton, Stack,
    Table, TableBody, TableCell, TableHead, TablePagination, TableRow,
    TextField, ToggleButton, ToggleButtonGroup, Tooltip, Typography,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import AddIcon from '@mui/icons-material/Add';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import CallReceivedIcon from '@mui/icons-material/CallReceived';
import CallMadeIcon from '@mui/icons-material/CallMade';
import axiosInstance from '../../../utils/axiosInstance';

const fontFamily = "'Plus Jakarta Sans', -apple-system, BlinkMacSystemFont, sans-serif";
const ACCENT = '#3b82f6';

const METHOD_LABELS = {
    CHECK: 'Check', ACH: 'ACH', WIRE: 'Wire', CARD: 'Card', CASH: 'Cash', OTHER: 'Other',
};

function formatCurrency(amount) {
    if (amount == null) return '$0.00';
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount);
}

const EMPTY_FORM = {
    float_type: 'COLLECTION',
    payment_method: 'ACH',
    amount: '',
    initiated_date: new Date().toISOString().slice(0, 10),
    counterparty: '',
    reference: '',
};

export default function FloatManagement() {
    const [view, setView] = useState('OUTSTANDING');
    const [metrics, setMetrics] = useState(null);
    const [records, setRecords] = useState([]);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(0);
    const [perPage, setPerPage] = useState(25);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [dialog, setDialog] = useState({ open: false, form: { ...EMPTY_FORM } });

    const fetchAll = useCallback(async () => {
        setError(null);
        setIsLoading(true);
        try {
            const params = { page: page + 1, per_page: perPage };
            if (view === 'OUTSTANDING') params.outstanding_only = true;
            if (view === 'CLEARED') params.cleared_only = true;

            const [m, r] = await Promise.all([
                axiosInstance.get('/api/finance/cash-position/float/metrics'),
                axiosInstance.get('/api/finance/cash-position/float', { params }),
            ]);
            if (m.data?.success) setMetrics(m.data.data);
            if (r.data?.success) {
                setRecords(r.data.data || []);
                setTotal(r.data.total || 0);
            }
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to load float data.');
        } finally {
            setIsLoading(false);
        }
    }, [page, perPage, view]);

    useEffect(() => { fetchAll(); }, [fetchAll]);

    const handleSave = async () => {
        try {
            await axiosInstance.post('/api/finance/cash-position/float', dialog.form);
            setDialog({ open: false, form: { ...EMPTY_FORM } });
            await fetchAll();
        } catch (err) {
            setError(err.response?.data?.message || 'Save failed.');
        }
    };

    const handleClear = async (id) => {
        try {
            await axiosInstance.post(`/api/finance/cash-position/float/${id}/clear`, {
                cleared_date: new Date().toISOString().slice(0, 10),
            });
            await fetchAll();
        } catch (err) {
            setError(err.response?.data?.message || 'Clear failed.');
        }
    };

    const totals = metrics?.totals || {};
    const breakdown = metrics?.breakdown || [];

    return (
        <Box sx={{ p: { xs: 2, md: 3 }, fontFamily, minHeight: '100vh', bgcolor: '#f8fafc' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 2 }}>
                <Box>
                    <Typography sx={{ fontFamily, fontWeight: 800, fontSize: '1.6rem', color: '#0f172a' }}>
                        Float Management
                    </Typography>
                    <Typography sx={{ fontFamily, fontSize: '0.85rem', color: '#64748b', mt: 0.5 }}>
                        Track collection vs. disbursement float and optimize the payment-method mix.
                    </Typography>
                </Box>
                <Stack direction="row" spacing={1.5}>
                    <Button
                        variant="contained"
                        startIcon={<AddIcon />}
                        onClick={() => setDialog({ open: true, form: { ...EMPTY_FORM } })}
                        sx={{ fontFamily, textTransform: 'none', borderRadius: '10px', bgcolor: ACCENT }}
                    >
                        Track Item
                    </Button>
                    <IconButton onClick={fetchAll}><RefreshIcon /></IconButton>
                </Stack>
            </Box>

            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

            {/* Top metrics */}
            <Grid container spacing={2.5} sx={{ mb: 3 }}>
                <Grid item xs={12} sm={6} md={4}>
                    <Card sx={{ borderRadius: '16px', border: '1px solid #e2e8f0', boxShadow: 'none' }}>
                        <CardContent sx={{ p: 2.5 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                                <CallReceivedIcon sx={{ color: '#10b981', fontSize: 18 }} />
                                <Typography sx={{ fontFamily, fontSize: '0.7rem', color: '#94a3b8', fontWeight: 700, textTransform: 'uppercase' }}>
                                    Collection Float
                                </Typography>
                            </Box>
                            <Typography sx={{ fontFamily, fontWeight: 800, fontSize: '1.5rem', color: '#10b981', mt: 0.5 }}>
                                {isLoading ? <Skeleton width={120} /> : formatCurrency(totals.collection_outstanding)}
                            </Typography>
                            <Typography sx={{ fontFamily, fontSize: '0.75rem', color: '#94a3b8' }}>
                                Outstanding deposits awaiting availability
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                    <Card sx={{ borderRadius: '16px', border: '1px solid #e2e8f0', boxShadow: 'none' }}>
                        <CardContent sx={{ p: 2.5 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                                <CallMadeIcon sx={{ color: '#ef4444', fontSize: 18 }} />
                                <Typography sx={{ fontFamily, fontSize: '0.7rem', color: '#94a3b8', fontWeight: 700, textTransform: 'uppercase' }}>
                                    Disbursement Float
                                </Typography>
                            </Box>
                            <Typography sx={{ fontFamily, fontWeight: 800, fontSize: '1.5rem', color: '#ef4444', mt: 0.5 }}>
                                {isLoading ? <Skeleton width={120} /> : formatCurrency(totals.disbursement_outstanding)}
                            </Typography>
                            <Typography sx={{ fontFamily, fontSize: '0.75rem', color: '#94a3b8' }}>
                                Outstanding outflows awaiting clearance
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                    <Card sx={{ borderRadius: '16px', border: '1px solid #e2e8f0', boxShadow: 'none' }}>
                        <CardContent sx={{ p: 2.5 }}>
                            <Typography sx={{ fontFamily, fontSize: '0.7rem', color: '#94a3b8', fontWeight: 700, textTransform: 'uppercase' }}>
                                Net Float Position
                            </Typography>
                            <Typography sx={{
                                fontFamily, fontWeight: 800, fontSize: '1.5rem', mt: 0.5,
                                color: (totals.net_float_position || 0) >= 0 ? '#10b981' : '#ef4444',
                            }}>
                                {isLoading ? <Skeleton width={120} /> : formatCurrency(totals.net_float_position)}
                            </Typography>
                            <Typography sx={{ fontFamily, fontSize: '0.75rem', color: '#94a3b8' }}>
                                Collection − Disbursement
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>

            {/* Breakdown by method */}
            <Card sx={{ borderRadius: '16px', border: '1px solid #e2e8f0', boxShadow: 'none', mb: 3 }}>
                <CardContent sx={{ p: 0 }}>
                    <Box sx={{ p: 2.5, borderBottom: '1px solid #e2e8f0' }}>
                        <Typography sx={{ fontFamily, fontWeight: 700, fontSize: '1rem' }}>
                            Average Days to Clear by Method
                        </Typography>
                    </Box>
                    {isLoading ? (
                        <Box sx={{ p: 3 }}><Skeleton variant="rounded" height={200} /></Box>
                    ) : (
                        <Table>
                            <TableHead>
                                <TableRow sx={{ '& th': { fontFamily, fontWeight: 700, color: '#475569', bgcolor: '#f8fafc' } }}>
                                    <TableCell>Type</TableCell>
                                    <TableCell>Method</TableCell>
                                    <TableCell align="right">Items</TableCell>
                                    <TableCell align="right">Total</TableCell>
                                    <TableCell align="right">Cleared</TableCell>
                                    <TableCell align="right">Avg Days</TableCell>
                                    <TableCell align="right">Outstanding</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {breakdown.length === 0 && (
                                    <TableRow>
                                        <TableCell colSpan={7} sx={{ textAlign: 'center', py: 4, fontFamily, color: '#94a3b8' }}>
                                            No float data yet.
                                        </TableCell>
                                    </TableRow>
                                )}
                                {breakdown.map((b, i) => (
                                    <TableRow key={i} hover>
                                        <TableCell>
                                            <Chip
                                                label={b.float_type}
                                                size="small"
                                                sx={{
                                                    fontFamily, fontSize: '0.7rem', fontWeight: 700,
                                                    bgcolor: b.float_type === 'COLLECTION' ? '#dcfce7' : '#fee2e2',
                                                    color: b.float_type === 'COLLECTION' ? '#166534' : '#991b1b',
                                                }}
                                            />
                                        </TableCell>
                                        <TableCell sx={{ fontFamily }}>
                                            {METHOD_LABELS[b.payment_method] || b.payment_method}
                                        </TableCell>
                                        <TableCell align="right" sx={{ fontFamily }}>{b.count}</TableCell>
                                        <TableCell align="right" sx={{ fontFamily, fontWeight: 700 }}>{formatCurrency(b.amount)}</TableCell>
                                        <TableCell align="right" sx={{ fontFamily }}>{b.cleared_count}</TableCell>
                                        <TableCell align="right" sx={{ fontFamily, fontWeight: 700 }}>
                                            {b.avg_days_to_clear != null ? `${b.avg_days_to_clear.toFixed(1)} d` : '—'}
                                        </TableCell>
                                        <TableCell align="right" sx={{ fontFamily, color: b.outstanding_amount > 0 ? '#f59e0b' : '#94a3b8' }}>
                                            {formatCurrency(b.outstanding_amount)}
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    )}
                </CardContent>
            </Card>

            {/* Records list */}
            <Card sx={{ borderRadius: '16px', border: '1px solid #e2e8f0', boxShadow: 'none' }}>
                <CardContent sx={{ p: 0 }}>
                    <Box sx={{ p: 2.5, display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid #e2e8f0', flexWrap: 'wrap', gap: 1 }}>
                        <Typography sx={{ fontFamily, fontWeight: 700, fontSize: '1rem' }}>Float Items</Typography>
                        <ToggleButtonGroup
                            value={view}
                            exclusive
                            size="small"
                            onChange={(_, v) => v && setView(v)}
                            sx={{ '& .MuiToggleButton-root': { fontFamily, textTransform: 'none', px: 2 } }}
                        >
                            <ToggleButton value="OUTSTANDING">Outstanding</ToggleButton>
                            <ToggleButton value="CLEARED">Cleared</ToggleButton>
                            <ToggleButton value="ALL">All</ToggleButton>
                        </ToggleButtonGroup>
                    </Box>
                    {isLoading ? (
                        <Box sx={{ p: 3 }}><Skeleton variant="rounded" height={200} /></Box>
                    ) : (
                        <>
                            <Table>
                                <TableHead>
                                    <TableRow sx={{ '& th': { fontFamily, fontWeight: 700, color: '#475569', bgcolor: '#f8fafc' } }}>
                                        <TableCell>Type</TableCell>
                                        <TableCell>Method</TableCell>
                                        <TableCell align="right">Amount</TableCell>
                                        <TableCell>Initiated</TableCell>
                                        <TableCell>Cleared</TableCell>
                                        <TableCell align="right">Days</TableCell>
                                        <TableCell>Counterparty</TableCell>
                                        <TableCell align="right">Action</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {records.length === 0 && (
                                        <TableRow>
                                            <TableCell colSpan={8} sx={{ textAlign: 'center', py: 4, fontFamily, color: '#94a3b8' }}>
                                                No items found.
                                            </TableCell>
                                        </TableRow>
                                    )}
                                    {records.map((r) => (
                                        <TableRow key={r.id} hover>
                                            <TableCell>
                                                <Chip
                                                    label={r.float_type}
                                                    size="small"
                                                    sx={{
                                                        fontFamily, fontSize: '0.7rem', fontWeight: 700,
                                                        bgcolor: r.float_type === 'COLLECTION' ? '#dcfce7' : '#fee2e2',
                                                        color: r.float_type === 'COLLECTION' ? '#166534' : '#991b1b',
                                                    }}
                                                />
                                            </TableCell>
                                            <TableCell sx={{ fontFamily }}>{METHOD_LABELS[r.payment_method] || r.payment_method}</TableCell>
                                            <TableCell align="right" sx={{ fontFamily, fontWeight: 700 }}>{formatCurrency(r.amount)}</TableCell>
                                            <TableCell sx={{ fontFamily, fontSize: '0.85rem' }}>{r.initiated_date}</TableCell>
                                            <TableCell sx={{ fontFamily, fontSize: '0.85rem' }}>{r.cleared_date || '—'}</TableCell>
                                            <TableCell align="right" sx={{ fontFamily }}>{r.days_outstanding != null ? r.days_outstanding : '—'}</TableCell>
                                            <TableCell sx={{ fontFamily, fontSize: '0.85rem' }}>{r.counterparty || '—'}</TableCell>
                                            <TableCell align="right">
                                                {!r.cleared_date && (
                                                    <Tooltip title="Mark as cleared today">
                                                        <IconButton size="small" onClick={() => handleClear(r.id)}>
                                                            <CheckCircleIcon fontSize="small" sx={{ color: '#10b981' }} />
                                                        </IconButton>
                                                    </Tooltip>
                                                )}
                                                {r.cleared_date && (
                                                    <CheckCircleIcon fontSize="small" sx={{ color: '#10b981', verticalAlign: 'middle' }} />
                                                )}
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                            <TablePagination
                                component="div"
                                count={total}
                                page={page}
                                onPageChange={(_, p) => setPage(p)}
                                rowsPerPage={perPage}
                                onRowsPerPageChange={(e) => { setPerPage(parseInt(e.target.value, 10)); setPage(0); }}
                                rowsPerPageOptions={[10, 25, 50, 100]}
                                sx={{ '& .MuiTablePagination-toolbar': { fontFamily } }}
                            />
                        </>
                    )}
                </CardContent>
            </Card>

            <Dialog open={dialog.open} onClose={() => setDialog({ open: false, form: { ...EMPTY_FORM } })} maxWidth="sm" fullWidth>
                <DialogTitle sx={{ fontFamily, fontWeight: 700 }}>Track New Float Item</DialogTitle>
                <DialogContent>
                    <Stack spacing={2} sx={{ mt: 1 }}>
                        <TextField
                            select
                            label="Float Type"
                            value={dialog.form.float_type}
                            onChange={(e) => setDialog({ ...dialog, form: { ...dialog.form, float_type: e.target.value } })}
                            fullWidth
                        >
                            <MenuItem value="COLLECTION">Collection (incoming)</MenuItem>
                            <MenuItem value="DISBURSEMENT">Disbursement (outgoing)</MenuItem>
                        </TextField>
                        <TextField
                            select
                            label="Payment Method"
                            value={dialog.form.payment_method}
                            onChange={(e) => setDialog({ ...dialog, form: { ...dialog.form, payment_method: e.target.value } })}
                            fullWidth
                        >
                            {Object.entries(METHOD_LABELS).map(([k, v]) => (
                                <MenuItem key={k} value={k}>{v}</MenuItem>
                            ))}
                        </TextField>
                        <TextField
                            label="Amount"
                            type="number"
                            value={dialog.form.amount}
                            onChange={(e) => setDialog({ ...dialog, form: { ...dialog.form, amount: e.target.value } })}
                            fullWidth
                        />
                        <TextField
                            label="Initiated Date"
                            type="date"
                            value={dialog.form.initiated_date}
                            onChange={(e) => setDialog({ ...dialog, form: { ...dialog.form, initiated_date: e.target.value } })}
                            InputLabelProps={{ shrink: true }}
                            fullWidth
                        />
                        <TextField
                            label="Counterparty"
                            value={dialog.form.counterparty}
                            onChange={(e) => setDialog({ ...dialog, form: { ...dialog.form, counterparty: e.target.value } })}
                            fullWidth
                        />
                        <TextField
                            label="Reference (e.g., check #)"
                            value={dialog.form.reference}
                            onChange={(e) => setDialog({ ...dialog, form: { ...dialog.form, reference: e.target.value } })}
                            fullWidth
                        />
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDialog({ open: false, form: { ...EMPTY_FORM } })} sx={{ fontFamily, textTransform: 'none' }}>Cancel</Button>
                    <Button
                        variant="contained"
                        onClick={handleSave}
                        disabled={!dialog.form.amount}
                        sx={{ fontFamily, textTransform: 'none', bgcolor: ACCENT }}
                    >
                        Save
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}
